import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Alert from 'antd/es/alert';
import { Radio, Space, Spin } from 'antd';
import axios from 'axios';

import { Query } from '../api/Query';
import { AuthorAdditionalInfo, getAuthorInfo } from '../api/Author';
import { AuthorRecommendation, getAuthorRecommendations } from '../api/AuthorRecommendation';
import { getDisplayName } from './shared';
import { AuthorInfoDisplay } from './AuthorInfoDisplay';
import { Personas } from './Personas';
import { BridgerRecommendations } from './BridgerRecommendations';

export const FocalAuthorDisplay = () => {
    const [focalAuthorId, setFocalAuthorId] = useState<string | undefined>();
    const [year, setYear] = useState<string>('2017'); // TODO: don't hard code year
    const [authorInfo, setAuthorInfo] = useState<AuthorAdditionalInfo>();
    const [authorRecs, setAuthorRecs] = useState<AuthorRecommendation | undefined>();
    const [loadingRecs, setLoadingRecs] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const location = useLocation();
    const history = useHistory();
    const [availableYears, setAvailableYears] = useState<string[]>([]);
    const [loadingYears, setLoadingYears] = useState<boolean>(false);
    const [loadingAuthor, setLoadingAuthor] = useState<boolean>(false);

    useEffect(() => {
        setLoadingYears(true);
        axios
            .get<string[]>('/api/available_years')
            .then((response) => response.data)
            .then((response) => {
                response.sort();
                setAvailableYears(response);
                setLoadingYears(false);
            });
    }, []);

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const year = queryParams.get('year') || '2017'; // TODO: don't hard code year
        setYear(year);
    }, [location]);

    useEffect(() => {
        const query = Query.fromQueryString(location.search);
        // ensure that we have the expected parameters in the URL
        if (query.isValid()) {
            setFocalAuthorId(query.focalAuthor);
        }
    }, [location, year]);

    useEffect(() => {
        if (focalAuthorId) {
            fetchAuthorRecs(focalAuthorId, year);
        }
    }, [focalAuthorId, year]);

    /**
     * Submits a call to the API for a given query as defined in the URL parameters
     * @returns {void}
     */
    const fetchAuthorRecs = (id: string, year: string): void => {
        // reset answers and errors before we go fetch new results
        setError(undefined);
        setAuthorRecs(undefined);
        setLoadingRecs(true);

        // Note: validation is occurring on the backend, so errors will be thrown if there are any invalid inputs
        getAuthorRecommendations(id, year)
            .then((responseAnswer) => {
                // When the API returns successfully update the answer
                setError(undefined);
                setAuthorRecs(responseAnswer);
            })
            .catch((err) => {
                if (err.response.status === 404 && err.response.data.redirect_author_id) {
                    const queryParams = new URLSearchParams(location.search);
                    queryParams.set('a', err.response.data.redirect_author_id);
                    location.search = queryParams.toString();
                    history.push(location);
                }
                // See if we can display as specific of an error as possible
                let error;
                if (err.response) {
                    // If the check below is true, the API returned
                    // error message that's likely helpful to display
                    if (err.response.data && err.response.data.error) {
                        error = err.response.data.error;
                    } else if (err.response.status === 503) {
                        error =
                            'Our system is a little overloaded, ' + 'please try again in a moment';
                    }
                }

                // Fallback to a general error message
                if (!error) {
                    error = 'Something went wrong. Please try again.';
                }
                setAuthorRecs(undefined);
                setError(error);
            })
            .finally(() => {
                setLoadingRecs(false);
            });
    };

    useEffect(() => {
        if (!focalAuthorId) {
            return;
        }
        setLoadingAuthor(true);
        getAuthorInfo(focalAuthorId, year).then((response) => {
            setAuthorInfo(response);
            setLoadingAuthor(false);
        });
    }, [focalAuthorId, year]);

    return (
        <React.Fragment>
            {loadingRecs || loadingYears || loadingAuthor ? (
                <span>
                    <Spin /> Loading…
                </span>
            ) : null}
            {!(loadingRecs || loadingYears || loadingAuthor) && focalAuthorId && authorInfo ? (
                <React.Fragment>
                    <Space size="small">
                        Using papers published since:
                        <Radio.Group
                            onChange={(e) => {
                                const queryParams = new URLSearchParams(location.search);
                                queryParams.set('year', e.target.value);
                                location.search = queryParams.toString();
                                history.push(location);
                            }}
                            defaultValue={() => {
                                const queryParams = new URLSearchParams(location.search);
                                const defaultYear = availableYears.sort().at(-1); // most recent year is default
                                const year = queryParams.get('year') || defaultYear;
                                return year;
                            }}
                            buttonStyle="solid">
                            {availableYears.map((year) => (
                                <Radio.Button key={year} value={year}>
                                    {year}
                                </Radio.Button>
                            ))}
                        </Radio.Group>
                    </Space>
                    <AuthorInfoDisplay
                        id={focalAuthorId}
                        _authorInfo={authorInfo}
                        hideAdditionalInfo
                    />
                    <Personas id={focalAuthorId} year={year} />
                    <BridgerRecommendations
                        name={getDisplayName(authorInfo)}
                        authorRecs={authorRecs}
                    />
                    {loadingRecs ? (
                        <div>
                            Loading recommendations... <Spin />
                        </div>
                    ) : null}
                </React.Fragment>
            ) : null}
            {error && !authorRecs ? (
                <Alert type="error" message={error || 'Sorry, something went wrong.'} />
            ) : null}
        </React.Fragment>
    );
};
